// Shared CSV serializer for every table-style export in the app.  Extracted
// from the inline `exportLeadsCSV` shape shipped 2026-07-27 in Leads.js so
// future callers (Invoices export, Receipts export, Reports rows-to-CSV,
// admin dumps, etc.) don't reinvent quoting + injection defense per site.
//
// Behavior:
//  - Every cell wrapped in double quotes (Excel-safe unconditional quoting;
//    stricter than the original Leads shape that only quoted when needed,
//    but produces output Excel opens identically).
//  - Internal double quotes escaped as "".
//  - CSV-injection defense: `csvSafeText`, the ONE rule for every CSV the
//    app writes (James 2026-10-21, "fix it everywhere"):
//      (a) a finite number, or a string that is strictly a plain decimal
//          (PLAIN_DECIMAL_RE: optional sign, digits, optional .digits; no
//          thousands separator or exponent), is written as-is, so -150.50
//          opens in Excel as a number, not the text "'-150.50";
//      (b) otherwise a value whose first character, after any leading
//          whitespace, is = + - @ TAB CR or LF gets one leading ' so
//          Excel/Sheets/Numbers don't evaluate it as a formula.
//    Applied BEFORE quote-escaping so the leading ' sits inside the quotes.
//    API twin: api/src/lib/csvSafe.js (same rule; CRA can't import from
//    api/src, hence the copy).  Both are pinned by one shared table,
//    testSupport/csvFormulaCases.json (csv.formulaRule.test.js here,
//    api/src/tests/csvSafe.test.js there) — change both together.
//  - `csvCell` = the API's csvCell (minimal RFC 4180 quoting: only a cell
//    holding , " ; CR or LF; the ';' because Excel with a ';' list
//    separator splits an unquoted 'Bob;=cmd…' and evaluates the rest).
//    Leads.js writes its cells through it; never a page-local quote test.
//  - null / undefined → empty string.
//  - Row separator: \r\n (Excel convention).
//  - Header row (column labels) always first.
//
// Written in CommonJS so the sanity-check via `node -e "require(...)"`
// works without a transpile step; webpack + ES `import { tableToCSV }`
// interop handles the frontend side.

const PLAIN_DECIMAL_RE = /^[-+]?\d+(\.\d+)?$/
const FORMULA_TRIGGER_RE = /^\s*[=+\-@\t\r\n]/

// The cell's text after the formula-injection rule, before quoting.
// null / undefined → ''.
function csvSafeText(value) {
  if (value === null || value === undefined) return ''
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  const s = String(value)
  if (PLAIN_DECIMAL_RE.test(s)) return s
  return FORMULA_TRIGGER_RE.test(s) ? "'" + s : s
}

// One cell, quoted only when needed — twin of api/src/lib/csvSafe.js csvCell.
function csvCell(value) {
  const s = csvSafeText(value)
  return /[",;\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
}

function escapeCell(value) {
  if (value === null || value === undefined) return '""'
  return '"' + csvSafeText(value).replace(/"/g, '""') + '"'
}

function tableToCSV(rows, columns) {
  const cols = columns || []
  const header = cols.map(c => escapeCell(c.label)).join(',')
  const body = (rows || []).map(row =>
    cols.map(c => escapeCell(row ? row[c.key] : '')).join(',')
  )
  return [header, ...body].join('\r\n')
}

function downloadCSV(filename, rows, columns) {
  const csv = tableToCSV(rows, columns)
  // ﻿ BOM so Excel opens UTF-8 correctly (matches the original
  // Leads.js behavior which prepended '﻿').
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
  // Lazy-require file-saver so node -e sanity checks of tableToCSV don't
  // pull in a browser-only dep.
  const { saveAs } = require('file-saver')
  saveAs(blob, filename)
}

module.exports = { tableToCSV, downloadCSV, escapeCell, csvCell, csvSafeText, PLAIN_DECIMAL_RE, FORMULA_TRIGGER_RE }
